import { useState } from 'react';
import { createFileRoute, useNavigate, Link } from '@tanstack/react-router';
import { useProject } from '../../graphql/queries';
import { ProjectForm } from '../../components/projects';
import { updateProject, ApiError, type UpdateProjectInput } from '../../lib/apiClient';
import { EmptyState } from '../../components/shared';
import '@tylertech/forge/icon';
import '@tylertech/forge/banner';
import './new.css';

export const Route = createFileRoute('/projects/$projectId/edit')({
  component: EditProjectPage,
});

export { EditProjectPage };

function EditProjectPage(): JSX.Element {
  const { projectId } = Route.useParams();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { data, loading, error: loadError } = useProject(projectId);

  const IconElement = 'forge-icon' as React.ElementType;
  const BannerElement = 'forge-banner' as React.ElementType;

  const project = data?.project;

  const handleSubmit = async (formData: UpdateProjectInput): Promise<void> => {
    setIsSubmitting(true);
    setError(null);
    try {
      await updateProject(projectId, formData);
      navigate({ to: '/projects/$projectId', params: { projectId } });
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : 'An unexpected error occurred. Please try again.'
      );
      setIsSubmitting(false);
    }
  };

  const handleCancel = (): void => {
    navigate({ to: '/projects/$projectId', params: { projectId } });
  };

  if (loading) {
    return (
      <div className="create-project-page">
        <p className="forge-typography--body1">Loading...</p>
      </div>
    );
  }

  if (loadError || !project) {
    return (
      <div className="create-project-page">
        <EmptyState
          icon="error"
          title="Failed to load project"
          description={loadError?.message ?? 'Project not found'}
        />
      </div>
    );
  }

  return (
    <div className="create-project-page">
      <div className="breadcrumb">
        <Link to="/projects/$projectId" params={{ projectId }} className="breadcrumb-link">
          <IconElement name="arrow_back" aria-hidden="true" />
          <span>Back to {project.name}</span>
        </Link>
      </div>

      <div className="page-header page-header--stacked">
        <h1 className="forge-typography--headline4">Edit Project</h1>
      </div>

      {error && (
        <BannerElement theme="error" dismissed={false} className="error-banner" role="alert">
          <div className="banner-content">
            <IconElement name="error" aria-hidden="true" />
            <span>{error}</span>
          </div>
        </BannerElement>
      )}

      <ProjectForm
        mode="edit"
        initialData={{
          name: project.name,
          description: project.description ?? '',
          status: project.status,
          ownerDivision: project.ownerDivision,
          ownerTeamId: project.ownerTeamId ?? '',
          startDate: project.startDate ?? '',
          targetEndDate: project.targetEndDate ?? '',
        }}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        isSubmitting={isSubmitting}
      />
    </div>
  );
}
